import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { environment } from '../../../environments/environment';
import { LegacyToken } from '@service/legacy/model/legacy-token';
import { LegacyLoginModel } from '@service/legacy/model/legacy-login-model';
import { WindowDetectService } from '@service/window-detect.service';
import { LegacySafeStorageService } from '@service/legacy/legacy-safe-storage.service';
import { LegacyMemberType, LegacyPlatformType } from '@service/legacy/model/legacy-extensions';

import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LegacyHttpClient {
  readonly v1URL = '/v1';
  readonly v2URL = '/v2';
  readonly v3URL = '/v3';
  readonly refreshUrl = `${environment.legacyApiURL}${this.v1URL}/tokens`;

  private readonly contentTypeHeader = 'Content-Type';
  private readonly contentUrlEncoded = 'application/x-www-form-urlencoded';
  private readonly contentJson = 'application/json';
  private readonly responseTypeArrayBuffer = 'arraybuffer';
  private readonly platformHeader = 'X-LATIV-PLATFORM';
  private readonly deviceIdHeader = 'DEVICE_ID';
  private readonly authHeader = 'AUTHORIZATION';
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly windowService = inject(WindowDetectService);
  private readonly isMobile = this.windowService.isMobileDevice();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object,
    private legacyStorageService: LegacySafeStorageService
  ) {}

  refreshToken(): Observable<LegacyToken> {
    const headers = this.appendBaseHeader(undefined, true);
    // 舊系統在M版固定寫死的參數.
    const model: LegacyLoginModel = {
      email: '',
      account: '',
      password: '',
      name: '',
      type: LegacyMemberType.EMAIL,
      ip: '127.0.0.1',
      os: 'android 6.0.1',
      code: '',
      token: '',
    };
    return this.http.put<LegacyToken>(this.refreshUrl, model, { headers }).pipe(
      map(res => {
        this.legacyStorageService.setLegacyToken(res);
        return res;
      })
    );
  }

  get<T>(url: string, params?: { params: { [param: string]: string } }, httpHeaders?: HttpHeaders): Observable<T> {
    return this.http.get<T>(`${environment.legacyApiURL}${url}`, {
      headers: this.appendBaseHeader(httpHeaders),
      params: Object.assign(params?.params ?? {}),
    });
  }

  post(
    url: string,
    paramObj?: any,
    fromBody = false,
    httpHeaders?: HttpHeaders,
    isArraybuffer: boolean = false
  ): Observable<any> {
    let headers = this.appendBaseHeader(httpHeaders);
    if (fromBody) {
      headers = this.appendContentType(headers, false);
    }
    const options: any = {
      headers,
    };
    if (isArraybuffer) {
      options.responseType = this.responseTypeArrayBuffer;
    }
    return this.http.post(`${environment.legacyApiURL}${url}`, this.getBody(paramObj, fromBody), options);
  }

  delete(url: string, paramObj?: any, fromBody: boolean = false, isJson: boolean = false): Observable<any> {
    url = `${environment.legacyApiURL}${url}`;
    let headers = this.appendBaseHeader();
    if (fromBody) {
      headers = this.appendContentType(headers, isJson);
      return this.http.delete(url, { body: this.getBody(paramObj, !isJson), headers });
    }
    return this.http.delete(url, { headers });
  }

  put(url: string, paramObj?: any, fromBody: boolean = false, isJson: boolean = false): Observable<any> {
    url = `${environment.legacyApiURL}${url}`;
    let headers = this.appendBaseHeader();
    if (fromBody) {
      headers = this.appendContentType(headers, isJson);
      return this.http.put(url, this.getBody(paramObj, !isJson), { headers });
    }
    return this.http.put(url, paramObj, { headers });
  }

  private appendBaseHeader(httpHeaders?: HttpHeaders, isRefresh = false): HttpHeaders {
    let headers = httpHeaders ?? new HttpHeaders();
    headers = headers.append(
      this.platformHeader,
      this.isMobile ? LegacyPlatformType.WebMobile.toString() : LegacyPlatformType.PC.toString()
    );
    if (!this.isBrowser) {
      return headers;
    }
    headers = headers.append(this.deviceIdHeader, this.legacyStorageService.getDeviceId());
    const token = this.legacyStorageService.getLegacyToken();
    if (isRefresh && token?.refreshToken) {
      headers = headers.append(this.authHeader, token.refreshToken);
    } else if (token?.accessToken) {
      headers = headers.append(this.authHeader, token.accessToken);
    }
    return headers;
  }

  private appendContentType(httpHeaders?: HttpHeaders, isJson = false): HttpHeaders {
    return (httpHeaders ?? new HttpHeaders()).append(
      this.contentTypeHeader,
      isJson ? this.contentJson : this.contentUrlEncoded
    );
  }

  private getBody(paramObj?: any, fromBody = false): any {
    return fromBody ? `=${paramObj}` : paramObj;
  }
}
